import { PlusOutlined } from '@ant-design/icons';
import { AutoComplete, Input, Spin, Tag } from 'antd';
import type React from 'react';
import type { District } from '@/generated/graphql/graphql';

type DistrictRowState = {
  districts: District[];
  inputVisible: boolean;
  isLoading: boolean;
  inputValue: string;
  unusedDistricts: District[];
};

type DistrictsInputState = {
  rowStates: Record<string, DistrictRowState>;
  handleClose: (district: District, driverId: string) => void | Promise<void>;
  handleInputConfirm: (
    districtId: string,
    driverId: string,
    label?: string
  ) => void | Promise<void>;
  handleInputChange: (value: string, driverId: string) => void;
  showInput: (driverId: string) => void;
};

export const DistrictsInputView: React.FC<{
  state: DistrictsInputState;
  driverId: string;
}> = ({ state, driverId }) => {
  const row: DistrictRowState = state.rowStates[driverId] || {
    districts: [],
    inputVisible: false,
    isLoading: false,
    inputValue: '',
    unusedDistricts: [],
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {row.districts.map((district: District) => {
        return (
          <Tag
            key={district.id}
            color={district.enabled ? 'blue' : 'gray'}
            closable
            onClose={(e) => {
              state.handleClose(district, driverId);
              e.preventDefault();
            }}
            style={{ fontSize: 14, margin: 0 }}
          >
            {district.name}
            {district.province?.name ? ` (${district.province.name})` : ''}
          </Tag>
        );
      })}
      <div>
        {row.inputVisible ? (
          <div className="tag-autocomplete-container">
            <AutoComplete
              allowClear
              style={{ height: 22 }}
              placeholder="Enter the district"
              options={row.unusedDistricts.map((district: District) => ({
                label: district.province?.name
                  ? `${district.name} (${district.province.name})`
                  : (district.name ?? ''),
                value: district.id,
              }))}
              filterOption={(inputValue, option) =>
                option?.label?.toString()?.toLowerCase()?.includes(inputValue.toLowerCase()) ??
                false
              }
              onChange={(value) => state.handleInputChange(value, driverId)}
              onSelect={
                row.isLoading
                  ? undefined
                  : (value, option) =>
                      state.handleInputConfirm(
                        String(value),
                        driverId,
                        option?.label?.toString?.() ?? String(value)
                      )
              }
              value={row.inputValue}
            >
              <Input className="tag-autocomplete-input" />
            </AutoComplete>
            {row.isLoading && (
              <Spin size="small" style={{ position: 'relative', top: 2, left: 4 }} />
            )}
          </div>
        ) : (
          <Tag onClick={() => state.showInput(driverId)} className="site-tag-plus">
            <PlusOutlined /> New Tag
          </Tag>
        )}
      </div>
    </div>
  );
};
